import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ApiErrorFactory } from '../../core/api-errors';
import { getCookie } from '../../core/cookie';
import { SftpKey, SftpKeyService, SftpPrivateKey } from '../../core/sftp';
import { ErrorDialog } from '../dialog';
import { DownloadKeyDialogComponent } from './download-key.component';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
@Component({
  selector: 'app-add-key-dialog',
  template: `
    <div class="add-key-dialog addkey-container">
      <form (ngSubmit)="generateKeyClick()">
        <mat-card appearance="outlined">
          <mat-card-header>
            <mat-card-title>{{ 'PAGES.SFTP.ADD_KEY_MODAL.TITLE' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <mat-form-field class="keyname--full-width" color="accent">
              <input
                matInput
                type="text"
                name="keyName"
                [required]="true"
                [ngModel]="keyName"
                (ngModelChange)="onKeyNameChange($event)"
                placeholder="{{ 'PAGES.SFTP.ADD_KEY_MODAL.DESCRIPTION' | translate }}"
                [disabled]="disableInput"
                />
            </mat-form-field>
          </mat-card-content>
          <mat-card-actions layout-align="end right">
            <button type="button" mat-button color="accent" (click)="closeDialog()">
              {{ 'PAGES.BACKUPS.BUTTONS.CANCEL' | translate }}
            </button>
    
            <button mat-raised-button type="submit" color="accent" [disabled]="disableButton">
              {{ 'PAGES.SFTP.ADD_KEY_MODAL.BUTTONS.GENERATE' | translate }}
            </button>
    
            @if (disableInput) {
              <mat-spinner
                class="info-spinner-small"
                color="accent"
                [diameter]="30"
                [strokeWidth]="4"
              ></mat-spinner>
            }
          </mat-card-actions>
        </mat-card>
      </form>
    </div>
    `,
  styleUrls: ['./add-key.styles.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddKeyDialogComponent implements OnInit {
  public keyName: string;
  public createdBy: string;
  public disableButton = true;
  public disableInput = false;
  @Output() keyListAdd: EventEmitter<SftpKey> = new EventEmitter<SftpKey>();
  private existingKeys: string[];
  private errorDialogRef: MatDialogRef<ErrorDialog>;

  constructor(
    public downloadDialog: MatDialog,
    public errorDialog: MatDialog,
    public dialogRef: MatDialogRef<AddKeyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private keyService: SftpKeyService,
    private apiErrorFactory: ApiErrorFactory,
    public readonly analyticsService: ProductAnalyticsService,
  ) {
    this.createdBy = getCookie('user_email');
  }

  ngOnInit(): void {
    this.keyService.getList().subscribe(
      (sftpKeys) => {
        this.existingKeys = sftpKeys.getKeyNames();
      },
      (err) => {
        console.error(err);
      },
    );
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public onKeyNameChange(keyName: string): void {
    this.disableButton = keyName.trim() === '';
    this.keyName = keyName;
  }

  public generateKeyClick(): void {
    this.analyticsService.trackEvent('sftp:generate-key', 'generate-sftp-key', 'clicked');
    this.disableButton = true;
    this.disableInput = true;
    this.dialogRef.disableClose = true;
    this.keyName = this.keyName.trim();
    if (this.validateKey(this.keyName)) {
      this.generateKey(this.keyName);
    } else {
      this.displayErrorMessage('Key name already exists: ' + this.keyName);
    }
  }

  onAddSuccess(key: SftpPrivateKey): void {
    this.dialogRef.close();
    this.downloadDialog.open(DownloadKeyDialogComponent, {
      data: { keyContent: key.content },
      disableClose: true,
      hasBackdrop: true,
    });
  }

  protected generateKey(keyName: string): void {
    this.keyService.generate(keyName, this.createdBy).subscribe(
      (key) => {
        this.keyListAdd.emit(new SftpKey(key.name, key.createdBy));
        this.onAddSuccess(key);
      },
      (err) => {
        this.handleError(err);
      },
    );
  }

  private validateKey(keyName: string): boolean {
    return this.existingKeys.indexOf(keyName) === -1;
  }

  private handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayErrorMessage(parsedError.getUserMessage());
  }

  private displayErrorMessage(message: string): void {
    this.errorDialogRef = this.errorDialog.open(ErrorDialog, {
      data: { errMsg: message },
    });
    this.disableButton = false;
    this.disableInput = false;
    this.dialogRef.disableClose = false;
  }
}
